import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, ShoppingCart, Truck, CheckCircle, XCircle, Clock, Store, User, Eye, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminLayout from "./AdminLayout";

const statusConfig = {
  pending: { label: "قيد الانتظار", badge: "bg-amber-100 text-amber-800 border-amber-200" },
  accepted: { label: "مقبول بالمحل", badge: "bg-blue-100 text-blue-800 border-blue-200" },
  preparing: { label: "جاري التحضير", badge: "bg-purple-100 text-purple-800 border-purple-200" },
  ready: { label: "جاهز للتسليم", badge: "bg-teal-100 text-teal-800 border-teal-200" },
  out_for_delivery: { label: "في الطريق للزبون", badge: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  delivered: { label: "تم التوصيل", badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "ملغي", badge: "bg-rose-100 text-rose-800 border-rose-200" }
};

export default function OrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewingOrder, setViewingOrder] = useState(null);

  const loadOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const response = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [search, statusFilter]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}`, { status: newStatus });
      toast.success("تم تحديث حالة الطلب فوراً");
      loadOrders();
      if (viewingOrder && viewingOrder.id === orderId) {
        setViewingOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDelete = async (orderId, shortCode) => {
    if (!window.confirm(`هل أنت متأكد من حذف الطلب #${shortCode} نهائياً؟`)) {
      return;
    }
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success("تم حذف الطلب بنجاح");
      loadOrders();
      if (viewingOrder?.id === orderId) setViewingOrder(null);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  return (
    <AdminLayout title="إدارة الطلبات" subtitle="متابعة طلبات التوصيل وتحديث حالاتها وتفاصيل المستلمين والمتاجر">
      <Card className="border border-border/80 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">سجل الطلبات</CardTitle>
              <CardDescription>إجمالي الطلبات المسجلة: {orders.length}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="بحث برقم الطلب، اسم الزبون، أو المتجر..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="تصفية حسب الحالة" />
              </SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">كافة الحالات</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2 md:hidden">
                {orders.map((order) => {
                  const shortCode = order.id ? order.id.slice(0, 8).toUpperCase() : "-";
                  const currentStatus = statusConfig[order.status] || { label: order.status, badge: "bg-gray-100" };
                  return (
                    <article key={order.id} className="rounded-2xl border border-border bg-card p-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-[10px] font-bold text-muted-foreground">#{shortCode}</p>
                          <h3 className="mt-1 truncate text-sm font-extrabold">{order.buyer_name || "زبون"}</h3>
                          <p className="mt-1 truncate text-xs text-muted-foreground">{order.shop_name || "متجر"}</p>
                        </div>
                        <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-medium ${currentStatus.badge}`}>{currentStatus.label}</span>
                      </div>
                      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                        <span className="font-mono text-xs font-bold">{Number(order.total || 0).toLocaleString()} ل.س</span>
                        <span className="text-[10px] text-muted-foreground">{order.created_at ? new Date(order.created_at).toLocaleDateString("ar-SY") : "-"}</span>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Button variant="outline" size="sm" className="h-10 flex-1 gap-1 text-xs" onClick={() => setViewingOrder(order)}>
                          <Eye className="h-3.5 w-3.5" /> التفاصيل
                        </Button>
                        <Select value={order.status} onValueChange={(val) => handleStatusChange(order.id, val)}>
                          <SelectTrigger className="h-10 flex-1 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent dir="rtl">
                            {Object.entries(statusConfig).map(([stKey, stConf]) => <SelectItem key={stKey} value={stKey}>{stConf.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </article>
                  );
                })}
                {orders.length === 0 && <div className="rounded-2xl bg-muted p-8 text-center text-sm text-muted-foreground">لا توجد أي طلبات مسجلة</div>}
              </div>
              <div className="hidden overflow-hidden rounded-xl border md:block">
                <table className="w-full text-sm text-right">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="p-3.5 font-semibold">رقم الطلب</th>
                      <th className="p-3.5 font-semibold">الزبون</th>
                      <th className="p-3.5 font-semibold">المتجر</th>
                      <th className="p-3.5 font-semibold">الإجمالي</th>
                      <th className="p-3.5 font-semibold">الحالة الحالية</th>
                      <th className="p-3.5 font-semibold">التاريخ</th>
                      <th className="p-3.5 font-semibold text-center">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {orders.map((order) => {
                      const shortCode = order.id ? order.id.slice(0, 8).toUpperCase() : "-";
                      const currentStatus = statusConfig[order.status] || { label: order.status, badge: "bg-gray-100" };
                      return (
                        <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                          <td className="p-3.5 font-mono text-xs font-bold text-foreground">
                            #{shortCode}
                          </td>
                          <td className="p-3.5">
                            <p className="font-semibold text-foreground text-xs">{order.buyer_name || "زبون"}</p>
                            <p className="font-mono text-[11px] text-muted-foreground mt-0.5" dir="ltr">{order.phone || "-"}</p>
                          </td>
                          <td className="p-3.5">
                            <span className="inline-flex items-center gap-1.5 text-xs text-foreground font-medium">
                              <Store className="w-3.5 h-3.5 text-muted-foreground" />
                              {order.shop_name || "متجر"}
                            </span>
                          </td>
                          <td className="p-3.5">
                            <span className="font-mono font-bold text-xs text-foreground">
                              {Number(order.total || 0).toLocaleString()} ل.س
                            </span>
                          </td>
                          <td className="p-3.5">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs border font-medium ${currentStatus.badge}`}>
                              {currentStatus.label}
                            </span>
                          </td>
                          <td className="p-3.5 text-xs text-muted-foreground">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString('ar-SY') : "-"}
                          </td>
                          <td className="p-3.5 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1 text-xs"
                                onClick={() => setViewingOrder(order)}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                التفاصيل
                              </Button>
                              <Select
                                value={order.status}
                                onValueChange={(val) => handleStatusChange(order.id, val)}
                              >
                                <SelectTrigger className="h-8 w-28 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent dir="rtl">
                                  {Object.entries(statusConfig).map(([stKey, stConf]) => (
                                    <SelectItem key={stKey} value={stKey}>{stConf.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">
                          لا توجد أي طلبات مسجلة
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      {viewingOrder && (
        <Dialog open={Boolean(viewingOrder)} onOpenChange={() => setViewingOrder(null)}>
          <DialogContent dir="rtl" className="sm:max-w-lg">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>تفاصيل الطلب #{viewingOrder.id?.slice(0, 8).toUpperCase()}</DialogTitle>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs border font-medium ${statusConfig[viewingOrder.status]?.badge}`}>
                  {statusConfig[viewingOrder.status]?.label || viewingOrder.status}
                </span>
              </div>
              <DialogDescription>
                تاريخ الطلب: {viewingOrder.created_at ? new Date(viewingOrder.created_at).toLocaleString('ar-SY') : "-"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/40 text-xs">
                <div>
                  <span className="text-muted-foreground block mb-0.5">الزبون:</span>
                  <span className="font-semibold text-sm">{viewingOrder.buyer_name || "-"}</span>
                  <p className="font-mono mt-0.5 text-muted-foreground" dir="ltr">{viewingOrder.phone || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-0.5">المتجر:</span>
                  <span className="font-semibold text-sm">{viewingOrder.shop_name || "-"}</span>
                </div>
              </div>

              <div>
                <span className="text-xs text-muted-foreground block mb-1">عنوان التوصيل:</span>
                <p className="text-sm font-medium p-2.5 rounded-lg border bg-card flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{viewingOrder.delivery_address || "كفرنبل - العنوان لم يحدد"}</span>
                </p>
              </div>

              {viewingOrder.note && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">ملاحظات الزبون:</span>
                  <p className="text-xs p-2.5 rounded-lg bg-amber-50 text-amber-900 border border-amber-200">
                    {viewingOrder.note}
                  </p>
                </div>
              )}

              <div>
                <span className="text-xs font-semibold text-muted-foreground block mb-2">العناصر المطلوبة:</span>
                <div className="border rounded-lg divide-y text-xs">
                  {(viewingOrder.items || []).map((item, idx) => (
                    <div key={idx} className="p-2.5 flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-foreground">{item.name}</span>
                        <span className="text-muted-foreground mr-2 font-mono">× {item.qty}</span>
                      </div>
                      <span className="font-mono font-medium">
                        {(Number(item.price || 0) * Number(item.qty || 1)).toLocaleString()} ل.س
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-card border space-y-1.5 text-xs">
                <div className="flex justify-between text-muted-foreground">
                  <span>المجموع الفرعي:</span>
                  <span className="font-mono">{(Number(viewingOrder.subtotal || 0)).toLocaleString()} ل.س</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>أجرة التوصيل:</span>
                  <span className="font-mono">{(Number(viewingOrder.delivery_fee || 0)).toLocaleString()} ل.س</span>
                </div>
                <div className="flex justify-between font-bold text-sm text-foreground pt-1.5 border-t">
                  <span>الإجمالي النهائي:</span>
                  <span className="font-mono text-primary">{(Number(viewingOrder.total || 0)).toLocaleString()} ل.س</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete(viewingOrder.id, viewingOrder.id.slice(0, 8))}
              >
                حذف الطلب
              </Button>
              <Button variant="outline" size="sm" onClick={() => setViewingOrder(null)}>
                إغلاق
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AdminLayout>
  );
}
